import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * An output template used by the saraa-templater: a single `.docx` template
 * file plus the meta shown on the generate page (name, description).
 *
 * The mapping of TemplateFields onto the document is intentionally out of scope
 * for now — a Template currently just links 1:1 to its `.docx` file. Managed in
 * the ERM admin and selected on the generate page.
 */
@Entity()
export class Template extends MongoEntity {
  static override _class = 'Template';
  static override _collectionName = 'templates';

  constructor(doc: PartialEntityData<Template>) {
    super(doc);
  }

  /**
   * The organization that owns this template. Only its members see it on the
   * generate page, and only its ORG_ADMINs can change it.
   */
  @Field(() => ID)
  organizationId: Maybe<string>;

  /** Human-readable name shown on the generate-page card. */
  @Field(() => String)
  name: Maybe<string>;

  /** Short description shown on the card. */
  @Field(() => String)
  description: Maybe<string>;

  /** ID of the uploaded `.docx` template file (one-to-one). */
  @Field(() => ID)
  templateFileId: Maybe<string>;

  /** Set by the ERM archive action — non-null means the template is archived. */
  @Field(() => Date)
  archivedAt?: Maybe<Date>;
}

/**
 * The only fields `updateTemplate` takes from the caller.
 *
 * `organizationId` is deliberately absent: ownership is decided when the
 * template is created and is what every permission check reads, so a save must
 * not be able to move a template into an organization the caller has no
 * authority over. `archivedAt` moves only through the archive endpoint.
 */
export const TEMPLATE_EDITABLE_FIELDS = ['name', 'description', 'templateFileId'] as const satisfies ReadonlyArray<
  keyof Template
>;
